import type { Address, Hex } from 'viem'
import {
  computeSelector,
  decodePermissions,
  formatAddress,
  getEffectiveFunctionSignatures,
  type AllowedCallEntry,
  type DataKeyEntry,
} from '../utils'
import { PERMISSION_NAMES, PERMISSION_CAPABILITY_GROUP, groupTagClasses, permissionPillClasses } from '../constants'
import type { CapabilityGroupKey } from '../constants'

export function ReviewConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  upAddress,
  controllerAddress,
  permissions,
  allowedCallEntries,
  allowedDataKeyEntries,
  isConfirmDisabled,
}: {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  upAddress: Address
  controllerAddress: Address
  permissions: bigint
  allowedCallEntries: AllowedCallEntry[]
  allowedDataKeyEntries: DataKeyEntry[]
  isConfirmDisabled?: boolean
}) {
  if (!isOpen) return null

  const permissionKeys = decodePermissions(permissions.toString())

  const pillClassesForPermission = (permKey: string) => {
    const group: CapabilityGroupKey = (PERMISSION_CAPABILITY_GROUP[permKey] ?? 'identitySigning') as CapabilityGroupKey
    return permissionPillClasses(groupTagClasses(group))
  }

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl rounded-xl border border-white/5 bg-surface shadow-2xl">
          <div className="px-6 py-5 border-b border-white/10">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-gray-100">Review permission updates</h3>
                <p className="text-sm text-gray-400 mt-1">
                  Verify the controller, permissions, and restrictions are correct.
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="px-6 py-6 space-y-5 max-h-[70vh] overflow-auto">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-4">
                <div className="text-xs text-gray-500">Universal Profile</div>
                <div className="mt-1 font-mono text-sm text-gray-200">{formatAddress(upAddress, 8)}</div>
              </div>
              <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-4">
                <div className="text-xs text-gray-500">Controller</div>
                <div className="mt-1 font-mono text-sm text-gray-200">{formatAddress(controllerAddress, 8)}</div>
              </div>
            </div>

            <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-200">Permissions</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {permissionKeys.length} selected
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {permissionKeys.map(p => (
                  <span
                    key={p}
                    className={pillClassesForPermission(p)}
                  >
                    {PERMISSION_NAMES[p] || p}
                  </span>
                ))}
                {permissionKeys.length === 0 && (
                  <span className="text-xs text-gray-500">No permissions selected</span>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-5">
              <div className="text-sm font-semibold text-gray-200">Restrictions</div>
              <div className="text-xs text-gray-500 mt-1">
                These are optional. If empty, the controller is not restricted by Allowed Calls or Allowed ERC725Y Data Keys.
              </div>

              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-4">
                  <div className="text-xs text-gray-500">Allowed Calls</div>
                  <div className="mt-1 text-sm font-medium text-gray-200">
                    {allowedCallEntries.length === 0 ? 'None' : `${allowedCallEntries.length} entr${allowedCallEntries.length === 1 ? 'y' : 'ies'}`}
                  </div>
                  {allowedCallEntries.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {allowedCallEntries.slice(0, 3).map(e => (
                        <div key={e.id} className="text-xs font-mono text-gray-400 truncate">
                          {summarizeAllowedCallEntry(e)}
                        </div>
                      ))}
                      {allowedCallEntries.length > 3 && (
                        <div className="text-xs text-gray-500">
                          +{allowedCallEntries.length - 3} more
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-4">
                  <div className="text-xs text-gray-500">Allowed Data Keys</div>
                  <div className="mt-1 text-sm font-medium text-gray-200">
                    {allowedDataKeyEntries.length === 0 ? 'None' : `${allowedDataKeyEntries.length} entr${allowedDataKeyEntries.length === 1 ? 'y' : 'ies'}`}
                  </div>
                  {allowedDataKeyEntries.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {allowedDataKeyEntries.slice(0, 3).map(e => (
                        <div key={e.id} className="text-xs text-gray-400 truncate">
                          <span className="font-medium text-gray-300">{e.name}</span>{' '}
                          <span className="font-mono text-gray-500">{shortKey(e.key as Hex)}</span>
                        </div>
                      ))}
                      {allowedDataKeyEntries.length > 3 && (
                        <div className="text-xs text-gray-500">
                          +{allowedDataKeyEntries.length - 3} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
              <div className="text-sm font-semibold text-amber-200">Security note</div>
              <div className="text-xs text-amber-200/90 mt-1">
                You are writing permissions onchain to your Universal Profile. Only authorise controllers you trust.
              </div>
            </div>
          </div>

          <div className="px-6 py-5 border-t border-white/10 flex items-center justify-end gap-2">
            <button onClick={onClose} className="btn-secondary">
              Back
            </button>
            <button onClick={onConfirm} className="btn-primary" disabled={isConfirmDisabled}>
              Confirm &amp; sign
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function summarizeAllowedCallEntry(entry: AllowedCallEntry): string {
  const types: string[] = []
  if (entry.callTypes.call) types.push('CALL')
  if (entry.callTypes.staticCall) types.push('STATICCALL')
  if (entry.callTypes.transferValue) types.push('TRANSFERVALUE')
  if (entry.callTypes.delegateCall) types.push('DELEGATECALL')

  const addr = entry.useAnyAddress ? 'any' : entry.address ? formatAddress(entry.address, 6) : 'unset'
  const iface = entry.useAnyInterface ? 'any' : entry.interfaceId || 'unset'
  const eff = getEffectiveFunctionSignatures(entry)
  let fn: string
  if (entry.useAnyFunction) fn = 'any'
  else if (eff.length === 0) fn = 'unset'
  else if (eff.length === 1) fn = computeSelector(eff[0]) || eff[0]
  else fn = `${eff.length} functions`

  return `${types.join('+') || 'NO_TYPE'} → ${addr} → ${iface} → ${fn}`
}

function shortKey(hex: Hex): string {
  if (!hex || hex === '0x') return hex
  if (hex.length <= 18) return hex
  return `${hex.slice(0, 10)}…${hex.slice(-6)}`
}

